import { UserGroup } from "../../../types/UserGroup";
import { isSuperAdmin, isInternal } from "../../../utils/permission";
import type { FeatureKey } from "../types";
import { FEATURE_TIERS } from "../types";

/**
 * Check if the current user/customer has access to a feature:
 * - Super Admin / Internal (incl. managed-service team) → full access.
 * - Self-service customers → gated by their subscription product name
 *   (video + blog are Sagelyn Pro only; Sagelyn Starter does not get them).
 *
 * Matched against subscription_product_name (e.g. "Sagelyn Pro"), case-insensitive
 * and substring-tolerant, because the backend stores product name, not a tier code.
 */
/** The plan name each backend tier code stands for in FEATURE_TIERS. */
const TIER_TO_PLAN: Record<string, string> = {
  pro: "sagelyn pro",
  basic: "sagelyn starter",
};

export function canAccessFeature(
  featureKey: FeatureKey,
  userGroups: UserGroup[] | string | undefined,
  subscriptionProductName: string | undefined,
  tier?: string
): boolean {
  if (isSuperAdmin(userGroups) || isInternal(userGroups)) {
    return true;
  }
  const allowed = FEATURE_TIERS[featureKey];
  if (!Array.isArray(allowed)) {
    return false;
  }
  // Preferred: the tier the backend derived from the product table. Legacy
  // product names (Daisy Pro, Daisy Scale, AI Reputation Manager…) never
  // contain "sagelyn pro", so the name test below hid every Pro feature from
  // done-for-you customers until 2026-09-18.
  const plan = tier ? TIER_TO_PLAN[tier.trim().toLowerCase()] : undefined;
  if (plan) {
    return allowed.some((p) => p.toLowerCase() === plan);
  }
  // Fallback for responses without a tier: substring match on the product name.
  if (!subscriptionProductName) {
    return false;
  }
  const name = subscriptionProductName.trim().toLowerCase();
  return allowed.some((p) => name.includes(p.toLowerCase()));
}
